import { PickSessionStrategy } from "@redotech/redo-fulfillment-wms-common/pick-session-enums";
import { locationIdFormat } from "@redotech/redo-ids/ids";
import { InventorySourceSchema } from "@redotech/redo-model/fulfillments/inventory-source";
import { AdvancedFilterInputSchema } from "@redotech/redo-model/outbound-labels/fulfillment-group-summary-input";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const createPickSession = {
  name: "createPickSession" as const,
  input: z.object({
    // Zero is permitted when the caller is consolidating one or more
    // handoffs (see consolidateHandoffSessionIds below). The handler
    // enforces that at least one of (numberOfGroups, handoff-consolidation)
    // produces work — otherwise the wave is empty and is rejected.
    numberOfGroups: z.number().int().min(0).max(100),
    searchInput: AdvancedFilterInputSchema,
    locationId: locationIdFormat.schema,
    strategy: createEnum(PickSessionStrategy),
    inventorySource: InventorySourceSchema.optional(),
    seedGroupId: zExt.objectId().nullish(),
    assignedUserId: zExt.objectId().optional(),
    // Set on the new session's `assigningUser` field. Callers that queue a
    // session for someone (or for themselves out-of-band, e.g. the bulk-
    // create-from-FG flow) pass this; the regular WMS create flow leaves
    // it unset.
    assigningUserId: zExt.objectId().optional(),
    toteIds: z.array(zExt.objectId()).optional(),
    pollForOpenSearchChanges: z.boolean().optional(),
    filterName: z.string().nullish(),
    useIncremental: z.boolean().optional(),
    // When true and strategy is MULTI_ITEM, allow single-item orders to be
    // included in the session by dropping the `expandedItemQuantity > 1`
    // strategy filter. Has no effect on SINGLE_ITEM sessions — those must
    // still enforce `expandedItemQuantity == 1` since multi-item orders
    // can't be picked under the single-item flow. Behaves the same as the
    // team-level forceMultiItemPickSessions setting, but per-call. Used by
    // hand-picked flows (bulk-from-table) where the caller has chosen
    // specific FG ids and shouldn't have them silently filtered.
    allowSingleItemInMultiSession: z.boolean().optional(),
    // Sequential zone picking: the zone the picker is stationed at. When
    // supplied (and the team is on `zonePickingMode === SEQUENTIAL` with a
    // zoned layout), the wave is filtered to FGs whose first zone matches
    // this id, items are tagged with their zone, and `pickSession.zoneId`
    // is set so completion/scope filters operate per-zone. Omitted today by
    // every caller — the picker-zone UI/profile pipe is wired separately.
    pickerZoneId: z.string().nullish(),
    // HANDED_OFF pick session ids the picker selected on the pickup prompt.
    // Their items/FGs/totes get folded into the new wave at create time and
    // the originals flip to CONSOLIDATED. Each must be at the same location
    // and zone as the new session; the server validates and refuses the
    // whole wave if any handoff is invalid. Empty / omitted means no
    // consolidation — fresh-only wave, identical to today's behavior.
    consolidateHandoffSessionIds: z.array(zExt.objectId()).optional(),
  }),
  output: z.object({
    pickSessionId: zExt.objectId().optional(),
    rejectedGroups: z
      .array(
        z.object({
          groupId: zExt.objectId(),
          orderNames: z.array(z.string()),
          reason: z.string(),
          productSku: z.string().optional(),
          productVariantTitle: z.string().optional(),
          productImageUrl: z.string().optional(),
        }),
      )
      .optional(),
  }),
};
